import { yCustomNodeDatas, ydoc } from "@/shared/config/yjs-config.ts";
import * as Y from 'yjs'
import {
    createListNodePage,
    appendPage,
    setCurrentPageIndex,
    readCurrentPageIndex,
    readPages,
    findPageIndexByAsset,
} from '@/shared/lib/list-node-pagination'
import { unlinkNodeAsset, readAssetRefsValue } from '@/entities/node/canvas-asset'
import type { GroupTaskExecutionContext, ListNodePage, NodeContextPayloadLike } from '@/shared/lib/node-registry'


/** 为上传/拖入的素材添加到当前页尾部。若尚无页面则新建一页 */
export function addUploadedAssetsPage(nodeId: string, assetIds: string[]): void {
    if (assetIds.length === 0) return

    const yNodeData = yCustomNodeDatas.get(nodeId) as Y.Map<unknown> | undefined
    if (!yNodeData) return

    ydoc.transact(() => {
        const pages = readPages(yNodeData)

        if (pages.length === 0) {
            // 尚无页：新建一页
            appendPage(yNodeData, createListNodePage({
                assetRefs: assetIds,
            }))
            setCurrentPageIndex(yNodeData, 0)
        } else {
            // 已有页：在当前页 assetRefs 尾部批量插入
            const currentIdx = readCurrentPageIndex(yNodeData)
            const targetIdx = currentIdx >= 0 && currentIdx < pages.length ? currentIdx : 0

            const page = { ...pages[targetIdx] }
            page.assetRefs = [...page.assetRefs, ...assetIds]
            const updated = [...pages]
            updated[targetIdx] = page
            yNodeData.set('pages', updated)
        }
    })
}

/** 批量删除节点中的素材：解除引用、同步分页、清理空页、修正 currentPageIndex */
export function deleteAssetsFromNode(nodeId: string, assetIds: string[]): void {
    const yNodeData = yCustomNodeDatas.get(nodeId) as Y.Map<unknown> | undefined
    if (!yNodeData) return

    // 先解除 assetRefs 引用（unlinkNodeAsset 内部有独立 transact）
    for (const assetId of assetIds) {
        unlinkNodeAsset(nodeId, assetId)
    }

    // 在同一事务中更新 pages + 修正 currentPageIndex
    ydoc.transact(() => {
        let pages = readPages(yNodeData)
        for (const assetId of assetIds) {
            const pageIdx = findPageIndexByAsset(pages, assetId)
            if (pageIdx !== -1) {
                const page = { ...pages[pageIdx] }
                const assetIdx = page.assetRefs.indexOf(assetId)
                if (assetIdx !== -1) {
                    const newAssetRefs = [...page.assetRefs]
                    newAssetRefs.splice(assetIdx, 1)
                    page.assetRefs = newAssetRefs
                }
                pages = [...pages.slice(0, pageIdx), page, ...pages.slice(pageIdx + 1)]
            }
        }

        // 判断节点状态，如果处于生成中，不要清理空页（避免把生成占位页清掉）
        const status = yNodeData.get('status') as string | undefined
        const isGenerating = status === 'pending' || status === 'running'

        // 清理空页
        const filtered = isGenerating ? pages : pages.filter((p) => p.assetRefs.length > 0)
        yNodeData.set('pages', filtered)

        // 修正 currentPageIndex：若超出范围则回退到最后有效页；若无页则置 -1
        const currentIdx = readCurrentPageIndex(yNodeData)
        if (filtered.length === 0) {
            if (currentIdx !== -1) setCurrentPageIndex(yNodeData, -1)
        } else if (currentIdx >= filtered.length) {
            setCurrentPageIndex(yNodeData, filtered.length - 1)
        }
    })
}

/**
 * 移除节点最后一页，如果它是空页（没有任何 assetRefs 也没有 failedRes）。
 * 用于取消生成任务时清理无效的空状态页。
 */
export function removeLastEmptyPage(nodeId: string): void {
    const yNodeData = yCustomNodeDatas.get(nodeId) as Y.Map<unknown> | undefined
    if (!yNodeData) return

    ydoc.transact(() => {
        const pages = readPages(yNodeData)
        if (pages.length === 0) return

        const lastPage = pages[pages.length - 1]
        const hasAssets = lastPage.assetRefs && lastPage.assetRefs.length > 0
        const hasFailedRes = lastPage.failedRefs && lastPage.failedRefs.length > 0

        if (!hasAssets && !hasFailedRes) {
            const newPages = pages.slice(0, pages.length - 1)
            yNodeData.set('pages', newPages)

            const currentIdx = readCurrentPageIndex(yNodeData)
            if (currentIdx >= newPages.length) {
                setCurrentPageIndex(yNodeData, newPages.length === 0 ? 0 : newPages.length - 1)
            }
        }
    })
}

/**
 * 从图集节点 data 中读取当前应展示的 assetRefs 列表。
 *
 * currentPageIndex >= 0 → 取 pages[currentPageIndex] 页内的 assetRefs；
 * currentPageIndex < 0 或不存在 → 回退到顶层的全量 assetRefs。
 *
 * @param data 节点的 Y.Map.toJSON() 快照数据
 * @returns 当前分页对应的 assetId 数组
 */
export function readListNodeAssetRefs(data: Record<string, unknown>, ignoreFailedRes: boolean = true): string[] {
    const currentPageIndex = typeof data.currentPageIndex === 'number' ? data.currentPageIndex : -1

    if (currentPageIndex >= 0) {
        const pages = Array.isArray(data.pages) ? data.pages as ListNodePage[] : []
        const page = pages[currentPageIndex]
        if (page) {
            const assetRefs = readAssetRefsValue(page.assetRefs, false)
            if (!ignoreFailedRes) return assetRefs
            // 过滤掉生成失败的素材，避免空占位 item 影响资产计数和分页逻辑
            const failedIds = new Set((page.failedRefs ?? []).map((r) => r.materialId))
            return assetRefs.filter((id) => !failedIds.has(id))
        }
        return []
    }

    return readAssetRefsValue(data.assetRefs, false)
}

/**
 * 拖拽排序：将当前页第 fromIndex 个素材移动到第 toIndex 个位置。
 * 仅在分页模式下生效（pages.length > 0 且 currentPageIndex >= 0）。
 */
export function reorderPageAssets(nodeId: string, fromIndex: number, toIndex: number): void {
    const yNodeData = yCustomNodeDatas.get(nodeId) as Y.Map<unknown> | undefined
    if (!yNodeData) return

    ydoc.transact(() => {
        const pages = readPages(yNodeData)
        const currentIdx = readCurrentPageIndex(yNodeData)
        if (currentIdx < 0 || currentIdx >= pages.length) return

        const page = { ...pages[currentIdx] }
        const arr = [...page.assetRefs]
        if (fromIndex < 0 || fromIndex >= arr.length) return
        if (toIndex < 0 || toIndex >= arr.length) return

        const [moved] = arr.splice(fromIndex, 1)
        arr.splice(toIndex, 0, moved)
        page.assetRefs = arr

        const updated = [...pages]
        updated[currentIdx] = page
        yNodeData.set('pages', updated)
    })
}

/**
 * replaceListNodeAssetAtIndex：替换图集当前展示列表里指定位置的素材引用。
 *
 * 只替换当前索引，不按 assetId 全量替换，避免同一素材复用时误改其他位置。
 */
export function replaceListNodeAssetAtIndex(nodeId: string, index: number, oldAssetId: string, newAssetId: string): boolean {
    const yNodeData = yCustomNodeDatas.get(nodeId) as Y.Map<unknown> | undefined
    if (!yNodeData || index < 0) return false

    let replaced = false

    ydoc.transact(() => {
        const pages = readPages(yNodeData)
        const currentIdx = readCurrentPageIndex(yNodeData)

        if (currentIdx >= 0 && currentIdx < pages.length) {
            const page = { ...pages[currentIdx] }
            const assetRefs = [...page.assetRefs]
            if (assetRefs[index] !== oldAssetId) return

            assetRefs[index] = newAssetId
            page.assetRefs = assetRefs
            const updatedPages = [...pages]
            updatedPages[currentIdx] = page
            yNodeData.set('pages', updatedPages)
            replaced = true
            return
        }

        const assetRefs = readAssetRefsValue(yNodeData.get('assetRefs'))
        if (assetRefs[index] !== oldAssetId) return

        const updatedAssetRefs = [...assetRefs]
        updatedAssetRefs[index] = newAssetId
        yNodeData.set('assetRefs', updatedAssetRefs)
        replaced = true
    })

    return replaced
}

export function isListNode(nodeType: string) {
    return ['text-list', 'gallery'].includes(nodeType)
}

export function readContextPayloadEstimatedItemCount(
    payload: NodeContextPayloadLike,
    groupExecution?: GroupTaskExecutionContext,
): number | undefined {
    const count = groupExecution?.estimatedItemCounts?.get(payload.nodeId)
    return typeof count === 'number' && Number.isFinite(count) && count > 0
        ? Math.ceil(count)
        : undefined
}

export function countContextPayloadItems(
    payload: NodeContextPayloadLike,
    groupExecution?: GroupTaskExecutionContext,
): number {
    const estimatedCount = readContextPayloadEstimatedItemCount(payload, groupExecution)
    if (estimatedCount) return estimatedCount

    if (payload.kind === 'image' || payload.kind === 'video' || payload.kind === 'audio') {
        return payload.resource?.url || payload.resource?.previewUrl ? 1 : 0
    }

    if (payload.kind === 'text') {
        const content = payload.data.content
        return typeof content === 'string' && content.trim().length > 0 ? 1 : 0
    }

    if (payload.kind === 'gallery' || payload.kind === 'text-list' || payload.kind === 'video-list' || payload.kind === 'audio-list') {
        return readListNodeAssetRefs(payload.data).length
    }

    return 0
}
